/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { useRef, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { buildErrorMsgFromForm, cleanPhoneNumber, getSum, isLoggedIn, stringSimilarity, validPhoneNumber } from '../../utils';
import { AppState } from '../../app.state';
import { get, orderBy, size, set } from 'lodash';
import { message, Modal, Table } from 'antd';
import Bugsnag from '@bugsnag/js';
import styles from '../styles';
import { Row } from '../../_shared/row';
import { Form } from '../../_shared/form';

import { Button } from '../../_shared/button';
import { createOrg, getOrgs } from '../../_shared/services/manage-orgs.service';
import { Org, _Org } from '../../_shared/interfaces/org';
import { StatesSelect } from '../../_shared/lib/states';
import { Equipment } from '../../_shared/interfaces/equipment';
import { Loading } from '../../_shared/icon';
import { renderAddress } from '../../dashboard/manage-orgs/manage-orgs.component';
import { updateUser } from '../../_shared/services/manage-users.service';
import { getUserData } from '../../authentication/authentication.actions';
import Logo from '../../_shared/logo';
import colors from '../../_shared/colors';
import { RegistrationFormType } from '..';

interface IProps {
  equipment?: Equipment;
  renderLoadedEquipment?: (s?: {}) => ReactNode;
  onRegistrationSubmit?: (formType: RegistrationFormType, data: any) => void;
}

interface IState {
  orgs: Org[];
  loading: boolean;
  loadingOrgs: boolean;
  matchModal: any;
  defaults?: any;
}

export const OrgCreate = (props: IProps) => {
  const loggedIn = useSelector(s => isLoggedIn(s as AppState));
  const user: any = useSelector(s => get(s, 'auth.user'));
  const hasOrg = useSelector(s => get(s, 'auth.user.orgId'))
  const isPhone = useSelector(s => get(s, 'dash.view.isPhoneBreak'));
  let [state, setState] = useState({
    loading: false,
    loadingOrgs: true,
    orgs: [],
    matchModal: {},
    defaults: undefined,
  } as IState)

  const dispatch: any = useDispatch();

  // AER-1754: take the form instance from the shared Form's `registerForm` callback —
  // the pattern every other screen in the app uses. This screen used to pass a `ref`
  // and then read `get(formRef, 'current.form')`, a property the forwardRef handle
  // never exposes (it resolves to `AddToForm(form)` itself, which already carries
  // validateFieldsAndScroll), so submit only ever worked via the `|| formRef.current`
  // fallback.
  const formRef = useRef<any>(null);
  const registerForm = useCallback((f: any) => { formRef.current = f; }, []);

  // AER-1754 (root cause): `defaults` MUST keep a stable identity across renders.
  // The shared Form re-applies `defaults` through setFieldsValue whenever the prop's
  // identity changes (src/_shared/form/index.tsx), and this object literal used to be
  // rebuilt on every render of this component. Any parent re-render — including the
  // setState({ loading: true }) that pressing Submit itself fires — therefore reset
  // `serviceOrgId` to undefined and wiped the user's Dental Distributor selection.
  // Submit then failed validation, silently, because of the `displayMessage` default
  // in finalize() below. Memoizing keeps the Form from touching the user's input, and
  // still re-applies the defaults when they genuinely change (a match-modal pick).
  const equipmentServiceOrgId = get(props, 'equipment.serviceOrgId');
  const formDefaults = useMemo(
    () => state.defaults || { serviceOrgId: equipmentServiceOrgId },
    [state.defaults, equipmentServiceOrgId]
  );

  const cleanVals = (vals = {}) => {
    let name = (get(vals, 'name', '') || '').toLowerCase();

    const remove = [
      'dental',
      'dentistry',
      'llc',
      'DDS',
      'DMD',
      'smile',
      'care',
      'health',
      'clinic',
      'family',
    ]

    remove.map(r => name = name.split(r).join(''))

    return {
      ...vals,
      name,
    }
  }

  const searchThroughOrgs = (vals = {}) => {
    vals = cleanVals(vals);

    const fields = [
      { key: 'name', val: (o: Org) => stringSimilarity(get(vals, 'name', '').toLowerCase(), o.name.toLowerCase()) },
      { key: 'address', val: (o: Org) => stringSimilarity(get(vals, 'address.address', '').toLowerCase(), `${get(o, 'address.address', '')}`.toLowerCase()) },
      { key: 'address.zip', val: `${get(vals, 'address.zip', '')}`.toLowerCase() },
    ]

    const ret = state.orgs.filter(o => o.orgType === 1).map(o => {
      const matches = fields.map(f => {
        if (typeof f.val == 'function') {
          return f.val(o);
        }
        return `${get(o, f.key, '')}`.toLowerCase() == f.val ? 1 : 0;
      })

      return {
        ...o,
        matches,
      }
    }).filter(o => getSum(o.matches) >= 0.5).sort((a, b) => getSum(b.matches) - getSum(a.matches))

    return ret;
  }

  const onSubmit = (e?: any, form?: any) => {
    const submitted = new Promise<void>((resolve, reject) => {
      setState(s => ({ ...s, loading: true }))

      // AER-1754: `displayMessage` used to default to false, so every validation
      // failure on this form was swallowed and a blocked submit was indistinguishable
      // from a dead button. Tell the user what is wrong by default.
      const finalize = (err?: any, displayMessage = true) => {
        setState(s => ({ ...s, loading: false }))

        if (!!err) {
          if (displayMessage) { message.error(err.message); }
          reject(err);
        } else {
          resolve()
        }
      }

      const FORM = form || formRef.current;

      if (!FORM) {
        return finalize(new Error('The form is still loading. Please try again in a moment.'));
      }

      FORM.validateFieldsAndScroll(async (err: any, vals: any) => {
        if (err) {
          const validationError = new Error(buildErrorMsgFromForm(err) || 'Please complete every required field.');
          // A blocked submit is user error, not a defect — see the terminal catch below.
          set(validationError, 'isValidationError', true);
          return finalize(validationError);
        }

        try {
          // AER-1619/AER-1754: antd v5+ hands nested fields back as flat dotted keys
          // ('address.address'), so re-nest before building the Org. Without this the
          // created record carries literal "address.address" keys — which the server's
          // writable-field whitelist drops — and a blank `address`.
          const nested = Object.keys(vals).reduce((acc, k) => set(acc, k, vals[k]), {} as any);

          const continueAndCreate = async () => {
            try {
              setState(s => ({ ...s, loading: true }))
              const org = new _Org({ ...(state.defaults || {}), ...nested });
              org.orgType = 1;
              org.createdBy = org.createdBy || user.email;
              org.updatedBy = user.email;
              await createOrg(org as Org);
              await updateUser({ ...user, role: !!state.defaults ? 4 : 2, orgId: org.id });
              await dispatch(getUserData());
              props.onRegistrationSubmit && props.onRegistrationSubmit('org', { org, user });
            } catch (err) {
              // axios rejects with an Error (see services/instance.ts); handing that
              // object straight to message.error() renders an object as a React child.
              message.error(get(err, 'message', 'Your organization could not be created. Please try again.'));
            } finally {
              setState(s => ({ ...s, loading: false }))
            }
          }

          const matches = searchThroughOrgs(nested);

          if (size(matches) && state.defaults === undefined) {
            setState(s => ({
              ...s,
              matchModal: {
                vals: nested,
                matches,
                visible: true,
                onRowClick: (r: Org) => {
                  FORM.setFieldsValue({ ...r });
                  state.defaults = r;
                  setState(s => ({ ...s, matchModal: {}, defaults: r }));
                  finalize();
                  onSubmit();
                },
                footer: null,
                okText: 'No match - my organization is different',
                onOk: async () => {
                  setState(s => ({ ...s, matchModal: {}, defaults: null }))
                  await continueAndCreate();
                  finalize();
                },
                onCancel: () => {
                  setState(s => ({ ...s, matchModal: {} }))
                  finalize();
                }
              }
            }))
          } else {
            await continueAndCreate();
            finalize();
          }
        } catch (err) {
          finalize(err, true);
        }
      })
    })

    // AER-1754: nothing awaits this promise — the Submit button and the shared Form's
    // Enter-key handler both discard it — so a rejected submit became an unhandled
    // rejection: invisible in the UI *and* in the console. finalize() has already told
    // the user; report anything that is not ordinary validation so it stops being
    // invisible to us too.
    submitted.catch((err: Error) => {
      if (get(err, 'isValidationError')) { return; }
      // Reporting must never become the thing that breaks the form: a throw in here
      // would just produce the unhandled rejection this handler exists to prevent.
      try { Bugsnag.notify(err); } catch (e) { /* noop */ }
    });

    return submitted;
  }

  const getAllOrgs = async () => {
    try {
      const orgs = await getOrgs();
      setState(s => ({ ...s, orgs }))
    } catch (err) {
      message.error(err);
    } finally {
      setState(s => ({ ...s, loadingOrgs: false }))
    }
  }

  useEffect(() => {
    if (loggedIn) {
      getAllOrgs();
    }
  }, [loggedIn])

  if (loggedIn && !hasOrg) {
    if (state.loadingOrgs) { return <Loading /> }

    const formProps = {
      registerForm,
      defaults: formDefaults,
      // onChange: (e: any, f: any) => setSignUp(f.getFieldsValue()),
      toDisplay: [
        {
          title: 'Organization Name',
          var: 'name',
          options: {
            rules: [
              {
                required: true,
                message: 'Organization Name is required. ',
              },
              {
                max: 50,
                message: 'Organization Name is limited to 50 characters. ',
              },
            ],
          },
        },
        {
          title: 'Dental Distributor',
          var: 'serviceOrgId',
          type: 'dropdown',
          opts: () => {
            return orderBy(state.orgs.filter(o => o.orgType === 0).map(o => ({ title: o.name, value: o.id })), ['title']);
          },
          options: {
            validateTrigger: 'onBlur',
            rules: [
              {
                required: true,
                message: 'Dental Distributor is required ',
              },
            ],
          },
        },
        {
          title: 'Phone',
          var: 'phone',
          transform: (value: string) => cleanPhoneNumber(value),
          options: {
            preserve: true,
            validateTrigger: 'onBlur',
            rules: [
              {
                // tslint:disable-next-line:no-any
                validator: (rule: any, value: string, cb: any) => {
                  const passes = !value || validPhoneNumber(value);
                  cb(passes ? undefined : rule.message);
                },
                message: 'Phone needs to be a valid phone number. ',
              },
            ],
          },
        },
        {
          title: 'Address',
          var: 'address.address',
          options: {
            validateTrigger: 'onBlur',
            rules: [
              {
                required: true,
                message: 'Address is required',
              },
            ],
          },
        },
        {
          title: 'Address 2',
          var: 'address.address2'
        },
        {
          title: 'City',
          var: 'address.city',
          options: {
            validateTrigger: 'onBlur',
            rules: [
              {
                required: true,
                message: 'City is required ',
              },
            ],
          },
        },
        {
          title: 'State',
          var: 'address.state',
          type: 'dropdown',
          opts: StatesSelect,
          style: `
            float: left;
            width: 50%;
            padding-right: 5px;
          `,
          options: {
            validateTrigger: 'onBlur',
            rules: [
              {
                required: true,
                message: 'State is required ',
              },
            ],
          },
        },
        {
          title: 'ZIP Code',
          var: 'address.zip',
          style: `
            float: left;
            width: 50%;
            padding-left: 5px;
          `,
          options: {
            validateTrigger: 'onBlur',
            rules: [
              {
                required: true,
                message: 'Zip Code is required ',
              },
            ],
          },
        },
      ],
      onSubmit,
    }

    return (
      <div css={css(styles.column)}>
        <Row center style={{ marginBottom: 20, maxWidth: 360 }}><Logo /></Row>
        <div css={css(styles.inputContainer)}>
          {props.renderLoadedEquipment && props.renderLoadedEquipment({ margin: -20, marginBottom: 20 })}
          <p css={css(styles.title)}>Organization Details</p>
          <p css={css(styles.subTitle, `font-style: italic;`)}>Please provide some additional details about the organization that you represent. This will allow us to group all of your equipment together under one account.</p>
          <Form {...formProps as any} />
          <Row center style={{ float: 'none', width: '100%' }}>
            <Button disabled={state.loading} loading={state.loading} title="Submit" style={{ marginRight: 10 }} onClick={e => { onSubmit(e); }} />
          </Row>
          <MatchModal {...state.matchModal} isPhone={isPhone} />
        </div>
      </div>
    )
  }

  return null;
}

const columns = [
  { title: 'Name', dataIndex: 'name', render: (t: string) => <span style={{ fontSize: 12 }}>{t || ''}</span> },
  { title: 'Phone', dataIndex: 'phone', render: (t: string) => <span style={{ fontSize: 12 }}>{t || ''}</span> },
  { title: 'Address', dataIndex: 'address', render: (t: string, r: Org) => <div style={{ fontSize: 12, flexDirection: 'row', display: 'flex' }}>{renderAddress(r)}</div> },
]

const phoneColumns = [
  {
    title: 'Name',
    dataIndex: 'name',
    render: (t: string, row: any) => {
      const items = [
        { render: () => <div><b>{get(row, 'name', '')}</b></div> },
        { render: () => <div>{get(row, 'phone', '')}</div> },
        { render: () => <div>{renderAddress(row)}</div> },
      ];

      return (
        <div style={{ borderBottom: `1px solid ${colors.inputBorder}`, paddingBottom: 15 }}>
          {items.map(c => {
            return (
              <div>{c.render()}</div>
            )
          })}
        </div>
      )
    }
  },
]

const MatchModal = (props: any) => {
  const { isPhone } = props;

  const onRowClick = (r: any) => {
    props.onRowClick && props.onRowClick(r)
  }

  const BUTTONS = [
    <Button outline title="Cancel" onClick={props.onCancel} style={isPhone ? { marginTop: 10 } : { marginRight: 10 }} />,
    <Button title={props.okText} onClick={props.onOk} />
  ]

  return (
    <Modal
      {...props}
      width={'90%'}
    >
      <div style={{ borderBottom: `1px solid ${colors.inputBorder}` }}>
        <p css={css(styles.title)}>Possible matches found</p>
        <p css={css(styles.subTitle, `font-style: italic;`)}>It looks like we may already have this organization in our system.  Please review the details below and make the appropriate selection.</p>
      </div>
      <div>
        <Table size="small" showHeader={!isPhone} dataSource={props.matches} columns={isPhone ? phoneColumns : columns} onRow={(record) => ({ onClick: () => onRowClick(record) })} />
      </div>
      <div style={props.isPhone ? { flexDirection: 'column', display: 'flex', alignItems: 'center', justifyContent: 'center' } : { flexDirection: 'row', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        {(isPhone ? BUTTONS.reverse() : BUTTONS).map(b => b)}
      </div>
    </Modal>
  )
}